import type {
  QuizBuilderBasicInfoSchema,
  QuizBuilderOptionsSchema,
  QuizBuilderSchema,
  QuizBuilderSectionSchema,
  QuizBuilderSectionsSchema,
  QuizBuilderUsersSchema,
} from '@repo/schemas/quiz/quizBuilder';
import type { QuizBuilderQuestionSchema } from '@repo/schemas/quiz/quizBuilderQuestion';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { quizBuilderBasicInfoSchema, quizBuilderOptionsSchema, quizBuilderSectionsSchema } from '@repo/schemas/quiz/quizBuilder';
import { QuizBuilderSteps } from '@repo/types/enums/quizBuilderSteps';
import { createContext, use, useEffect, useMemo } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import type { QuizBuilderContextActions, QuizBuilderContextStates } from '@/context/quiz-builder';
import type { QuizBuilderData } from '@/hooks/quiz';

import { useQuizBuilderData } from '@/hooks/quiz';
import { findFirstErrorPathFromZod, formErrorToast } from '@/utils/form/formErrorToast';

import type { QuizBuilderContextProviderProps } from '.';

export const QuizBuilderContext = createContext<(QuizBuilderContextStates & QuizBuilderContextActions) | null>(null);

export const useQuizBuilder = () => {
  const context = use(QuizBuilderContext);
  if (!context) {
    throw new Error('useQuizBuilder must be used within a QuizBuilderProvider');
  }
  return context;
};

export function QuizBuilderContextProvider(quizBuilderContextProviderProps: QuizBuilderContextProviderProps) {
  const { children, quizId, navigateSection } = quizBuilderContextProviderProps;
  const location = useLocation();

  const { quizData, quizQuery, updateQuizDraftData, removeQuizDraftData } = useQuizBuilderData(quizId);

  const currentStep = useMemo<QuizBuilderSteps>(() => {
    return Object.values(QuizBuilderSteps).find((step) => location.pathname.includes(step.toLowerCase())) || QuizBuilderSteps.BASIC_INFO;
  }, [location.pathname]);

  const basicInfoForm = useForm<QuizBuilderBasicInfoSchema>({
    defaultValues: {
      name: quizData.name,
      desc: quizData.desc,
    },
    mode: 'onChange',
    resolver: standardSchemaResolver(quizBuilderBasicInfoSchema),
  });

  const sectionsForm = useForm<QuizBuilderSectionsSchema>({
    defaultValues: {
      content: quizData.content || [],
    },
    mode: 'onChange',
    resolver: standardSchemaResolver(quizBuilderSectionsSchema),
  });

  const sectionsFieldArray = useFieldArray<QuizBuilderSectionsSchema>({
    control: sectionsForm.control,
    name: 'content',
  });

  const optionsForm = useForm<QuizBuilderOptionsSchema>({
    defaultValues: {
      maxAttempts: quizData.maxAttempts ?? 0,
      time: quizData.time ?? 0,
      freeNavigation: quizData.freeNavigation ?? false,
      instantMark: quizData.instantMark ?? true,
      deadline: quizData.deadline,
      available: quizData.available,
      gradeMapping: quizData.gradeMapping ?? null,
    },
    mode: 'onChange',
    resolver: standardSchemaResolver(quizBuilderOptionsSchema),
  });

  const usersForm = useForm<QuizBuilderUsersSchema>({
    defaultValues: {
      users: quizData.users ?? [],
      groups: quizData.groups ?? [],
    },
  });

  const sections: QuizBuilderSectionSchema[] = useWatch({
    name: 'content',
    control: sectionsForm.control,
  });

  const totalQuestions: number = sections.reduce((acc, current) => acc + (current.questions.length || 0), 0);

  const buildQuizPayload = (): QuizBuilderSchema => {
    return {
      ...basicInfoForm.getValues(),
      ...sectionsForm.getValues(),
      ...optionsForm.getValues(),
    };
  };

  const submitBasicInfo = (data: QuizBuilderBasicInfoSchema) => {
    updateQuizDraftData({
      ...data,
    });
    navigateSection(QuizBuilderSteps.QUESTIONS);
  };

  const submitSections = (data: QuizBuilderSectionsSchema) => {
    updateQuizDraftData({
      ...data,
    });
    navigateSection(QuizBuilderSteps.SETTINGS);
  };

  const submitOptions = (data: QuizBuilderOptionsSchema) => {
    updateQuizDraftData({
      ...data,
    });
  };

  const appendSection = (name?: string) => {
    const number: number = sectionsFieldArray.fields.length + 1;
    const newSection: QuizBuilderSectionSchema = {
      id: uuidv4(),
      name: name || `Sekcja ${number}`,
      questions: [],
    };
    sectionsFieldArray.append(newSection);
    saveDraftData();
  };

  const duplicateSection = (sectionIndex: number) => {
    const current = sectionsForm.getValues('content');
    const original = current[sectionIndex];
    if (!original) return;

    const copy: QuizBuilderSectionSchema = {
      ...original,
      id: uuidv4(),
      name: `${original.name} (kopia)`,
    };

    const insertIndex = Math.min(sectionIndex + 1, current.length);
    const next = [...current];
    next.splice(insertIndex, 0, copy);

    sectionsForm.setValue('content', next);
    saveDraftData();
  };

  const removeSection = (sectionIndex: number) => {
    sectionsFieldArray.remove(sectionIndex);
    saveDraftData();
  };

  const appendQuestion = (data: QuizBuilderQuestionSchema, sectionIndex: number, insertIndex?: number) => {
    const currentQuestions = sections[sectionIndex]?.questions ?? [];
    if (insertIndex !== undefined && insertIndex >= 0) {
      // Insert at specific position
      const newQuestions = [...currentQuestions];
      newQuestions.splice(insertIndex, 0, data);
      sectionsForm.setValue(`content.${sectionIndex}.questions`, newQuestions);
    } else {
      // Append to end (default behavior)
      sectionsForm.setValue(`content.${sectionIndex}.questions`, [...currentQuestions, data]);
    }
    saveDraftData();
  };

  const duplicateQuestion = (sectionIndex: number, questionIndex: number) => {
    const current = sectionsForm.getValues(`content.${sectionIndex}.questions`);
    const original = current[questionIndex];
    if (!original) return;
    const copy = JSON.parse(JSON.stringify(original));
    copy.id = uuidv4();
    const insertIndex = Math.min(questionIndex + 1, current.length);
    const next = [...current];
    next.splice(insertIndex, 0, copy);
    sectionsForm.setValue(`content.${sectionIndex}.questions`, next);
    saveDraftData();
  };
  const removeQuestion = (sectionIndex: number, questionIndex: number) => {
    const section = sections[sectionIndex];
    if (!section) return;
    sectionsForm.setValue(`content.${sectionIndex}.questions`, [...section.questions.filter((_, i) => i !== questionIndex)]);
    saveDraftData();
  };

  const saveDraftData = () => {
    const data: Partial<QuizBuilderData> = {
      ...basicInfoForm.getValues(),
      ...sectionsForm.getValues(),
      ...optionsForm.getValues(),
      ...usersForm.getValues(),
      currentStep: currentStep,
    };
    updateQuizDraftData(data);
  };

  const canNavigateToStep = async (to: QuizBuilderSteps): Promise<boolean> => {
    switch (to) {
      case QuizBuilderSteps.BASIC_INFO:
        return true;
      case QuizBuilderSteps.QUESTIONS: {
        return new Promise<boolean>((resolve) => {
          basicInfoForm.handleSubmit(
            () => {
              resolve(true);
            },
            () => {
              formErrorToast(basicInfoForm, quizBuilderBasicInfoSchema, 'Nie można przejść dalej. Sprawdź poprawność danych.');
              resolve(false);
            }
          )();
        });
      }
      case QuizBuilderSteps.SETTINGS: {
        return new Promise<boolean>((resolve) => {
          sectionsForm.handleSubmit(
            () => {
              resolve(true);
            },
            () => {
              const result = formErrorToast(sectionsForm, quizBuilderSectionsSchema, 'Nie można przejść dalej. Sprawdź poprawność danych. Błędnie skonstruowane pytania zostały podkreślone.');
              if (result?.error) {
                navigateSection(QuizBuilderSteps.QUESTIONS);
                const firstErrorPath = findFirstErrorPathFromZod(result.error);
                if (firstErrorPath) {
                  setTimeout(() => scrollToFirstError(firstErrorPath), 300);
                }
              }
              resolve(false);
            }
          )();
        });
      }
      default:
        return false;
    }
  };

  const handleNavigateStep = async (to: QuizBuilderSteps): Promise<boolean> => {
    if (await canNavigateToStep(to)) {
      navigateSection(to);
      return true;
    }
    return false;
  };

  const submitBasicInfoForm = () => {
    basicInfoForm.handleSubmit(submitBasicInfo, () => {
      formErrorToast(basicInfoForm, quizBuilderBasicInfoSchema, 'Wystąpił błąd podczas zapisywania. Sprawdź poprawność danych.');
    })();
  };

  const submitSectionsForm = () => {
    sectionsForm.handleSubmit(submitSections, () => {
      formErrorToast(sectionsForm, quizBuilderSectionsSchema, 'Wystąpił błąd podczas zapisywania. Sprawdź poprawność danych. Błędnie skonstruowane pytania zostały podkreślone.');
    })();
  };

  /**
   * Scrolls to the first error element based on the error path
   * Example: "content.0.questions.1.content" -> scrolls to section 0, question 1
   */
  const scrollToFirstError = (errorPath: string) => {
    const pathParts = errorPath.split('.');
    const contentIndex = pathParts.indexOf('content');
    if (contentIndex === -1 || contentIndex + 1 >= pathParts.length) return;

    const sectionIndexStr = pathParts[contentIndex + 1];
    if (sectionIndexStr === undefined) return;
    const sectionIndex = Number.parseInt(sectionIndexStr, 10);
    if (Number.isNaN(sectionIndex)) return;

    const questionsIndex = pathParts.indexOf('questions', contentIndex + 1);
    const questionIndexStr = questionsIndex !== -1 && questionsIndex + 1 < pathParts.length ? pathParts[questionsIndex + 1] : undefined;
    const questionIndex = questionIndexStr !== undefined ? Number.parseInt(questionIndexStr, 10) : null;

    const sectionElement = document.querySelector<HTMLElement>(`[data-section-index="${sectionIndex}"]`);
    if (!sectionElement) return;

    sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const highlight = (el: HTMLElement) => {
      el.classList.add('ring-2', 'ring-fill-destructive/60');
      setTimeout(() => el.classList.remove('ring-2', 'ring-fill-destructive/60'), 3000);
    };

    if (questionIndex !== null && !Number.isNaN(questionIndex)) {
      setTimeout(() => {
        const questionElement = document.querySelector<HTMLElement>(`[data-section-index="${sectionIndex}"][data-question-index="${questionIndex}"]`);
        if (questionElement) {
          questionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          highlight(questionElement);
        } else {
          highlight(sectionElement);
        }
      }, 400);
    } else {
      setTimeout(() => highlight(sectionElement), 100);
    }
  };

  //update section form on change
  useEffect(() => {
    sectionsForm.trigger();
  }, [sections]);

  useEffect(() => {
    if (!quizQuery.isSuccess) return;

    const remote = quizQuery.data;
    const localTs: number = new Date(quizData.timestamp).getTime();
    const remoteTs: number = new Date(remote.updatedAt).getTime();

    if (remoteTs <= localTs) return;

    basicInfoForm.reset({
      name: remote.name,
      desc: remote.desc,
    });
    sectionsForm.reset({
      //schema vs type definition mismatch , build error without it
      content: remote.content as QuizBuilderSectionSchema[],
    });
    optionsForm.reset({
      maxAttempts: remote.maxAttempts,
      time: remote.time ?? 0,
      freeNavigation: remote.freeNavigation,
      instantMark: remote.instantMark,
      deadline: remote.deadline,
      available: remote.available,
      gradeMapping: remote.gradeMapping ?? null,
    });
    usersForm.reset({
      users: remote.users,
      groups: remote.groups,
    });
  }, [quizQuery.data, quizQuery.isSuccess, quizQuery.isFetching, quizData.timestamp]);

  return (
    <QuizBuilderContext
      value={{
        totalQuestions,
        sections,
        sectionsFieldArray,
        currentStep,
        basicInfoForm,
        sectionsForm,
        optionsForm,
        usersForm,
        quizQuery,
        submitBasicInfoForm,
        submitSectionsForm,
        saveDraftData,
        canNavigateToStep,
        updateQuizDraftData,
        submitBasicInfo,
        submitSections,
        submitOptions,
        buildQuizPayload,
        handleNavigateStep,
        appendSection,
        removeSection,
        appendQuestion,
        removeQuestion,
        duplicateQuestion,
        duplicateSection,
        removeQuizDraftData,
      }}
    >
      {children}
    </QuizBuilderContext>
  );
}
